import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  Clapperboard,
  List,
  LayoutGrid,
  MoreHorizontal,
  Search,
  Pencil,
  Archive,
  Trash2,
  ChevronDown,
  Check,
  Calendar,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { AppApiError, appFetch, appApiBaseUrl } from '@/shared/api/app-client'
import type { ApiResponse, PaginatedResponse, ProjectListItem } from '@/shared/api/app-types'
import { cn } from '@/shared/ui/cn'
import { getAllProjects, loadProjectThumbnail } from '@/infrastructure/storage'
import { ensureClipWorkspaceRoot } from '@/features/app-shell/utils/clip-render'

/** 将 /uploads/... 相对路径补全为后端完整 URL */
function resolveCoverUrl(url: string | null): string | null {
  if (!url) return null
  if (url.startsWith('/uploads/')) {
    return `${appApiBaseUrl}${url}`
  }
  return url
}

/** 项目类型 → 中文标签 */
const PROJECT_TYPE_LABELS: Record<string, string> = {
  clips: '片段',
  captions: '字幕',
  dubbing: '配音',
  editor: '编辑器',
  video_cut: '视频剪辑',
}

export function projectTypeLabel(type: string): string {
  return PROJECT_TYPE_LABELS[type] ?? type
}

/** 筛选选项定义 */
const TYPE_OPTIONS = [
  { value: '', label: '全部类型' },
  { value: 'clips', label: '片段' },
  { value: 'captions', label: '字幕' },
  { value: 'dubbing', label: '配音' },
  { value: 'video_cut', label: '视频剪辑' },
]

const STATUS_OPTIONS = [
  { value: '', label: '全部状态' },
  { value: 'draft', label: '草稿' },
  { value: 'processing', label: '处理中' },
  { value: 'completed', label: '已完成' },
  { value: 'failed', label: '失败' },
  { value: 'archived', label: '已归档' },
]

const SOURCE_OPTIONS = [
  { value: '', label: '全部来源' },
  { value: 'upload', label: '上传' },
  { value: 'url', label: '链接' },
  { value: 'imported', label: '导入' },
  { value: 'library', label: '素材库' },
]

const SORT_OPTIONS = [
  { value: 'updated', label: '按修改时间' },
  { value: 'created', label: '按创建时间' },
  { value: 'title', label: '按名称' },
]

export function ProjectsCanvas({
  initialTypeFilter = '',
}: {
  initialTypeFilter?: '' | 'clips' | 'captions' | 'dubbing' | 'video_cut'
}) {
  const navigate = useNavigate()
  const [projects, setProjects] = useState<ProjectListItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [refreshKey, setRefreshKey] = useState(0)
  const [linkedEditorProjects, setLinkedEditorProjects] = useState<Record<number, string>>({})
  /** 本地编辑器项目（无 remoteProjectId），用负数 ID 标记，映射回本地项目 ID */
  const [localProjectMap, setLocalProjectMap] = useState<Record<number, string>>({})

  const refresh = () => setRefreshKey((k) => k + 1)

  // 筛选状态
  const [filterType, setFilterType] = useState<string>(initialTypeFilter)
  const [filterStatus, setFilterStatus] = useState('')
  const [filterSource, setFilterSource] = useState('')
  const [sortBy, setSortBy] = useState('updated')
  const [searchText, setSearchText] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [datePopoverOpen, setDatePopoverOpen] = useState(false)
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid')

  useEffect(() => {
    setFilterType(initialTypeFilter)
  }, [initialTypeFilter])

  // 搜索防抖
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(searchText)
    }, 300)
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current)
    }
  }, [searchText])

  const goToProject = useCallback((project: ProjectListItem) => {
    // 本地编辑器项目（负数 ID）-> 直接跳编辑器
    if (project.id < 0) {
      const localId = localProjectMap[project.id]
      if (localId) {
        navigate({ to: '/editor/$projectId', params: { projectId: localId } })
        return
      }
    }

    const linkedEditorProjectId = linkedEditorProjects[project.id]
    if (project.type === 'video_cut' && linkedEditorProjectId) {
      navigate({ to: '/editor/$projectId', params: { projectId: linkedEditorProjectId } })
      return
    }
    navigate({ to: '/app/projects/$projectId', params: { projectId: String(project.id) } })
  }, [linkedEditorProjects, localProjectMap, navigate])

  const loadLinkedEditorProjects = useCallback(async (items: ProjectListItem[]) => {
    const wsStatus = await ensureClipWorkspaceRoot().catch(() => ({ ok: false as const }))
    if (!wsStatus.ok) {
      setLinkedEditorProjects({})
      setLocalProjectMap({})
      return
    }

    try {
      const localProjects = await getAllProjects()

      // 1. 有 remoteProjectId 的本地项目 -> 建映射（不重复显示）
      const nextMap = localProjects.reduce<Record<number, string>>((acc, project) => {
        if (typeof project.remoteProjectId === 'number') {
          acc[project.remoteProjectId] = project.id
        }
        return acc
      }, {})
      setLinkedEditorProjects(nextMap)

      // 2. 无 remoteProjectId 的纯本地项目 -> 转成 ProjectListItem 合并到列表
      const pureLocalProjects = localProjects.filter((p) => p.remoteProjectId === undefined)
      const nextLocalMap: Record<number, string> = {}
      const localItems: ProjectListItem[] = []

      for (let i = 0; i < pureLocalProjects.length; i++) {
        const project = pureLocalProjects[i]
        if (!project) continue
        const fakeId = -(i + 1) // 负数 ID 避免和后端冲突
        nextLocalMap[fakeId] = project.id

        // 加载缩略图
        let coverUrl: string | null = null
        try {
          const blob = await loadProjectThumbnail(project.id)
          if (blob) coverUrl = URL.createObjectURL(blob)
        } catch {
          // ignore
        }

        localItems.push({
          id: fakeId,
          projectNo: '本地',
          title: project.name || '未命名项目',
          type: 'video_cut',
          status: 'draft',
          durationMs: project.duration || null,
          coverUrl,
          sourceInputType: null,
          sourceInputUrl: null,
          primaryLanguage: null,
          aspectRatio: project.metadata
            ? `${project.metadata.width}x${project.metadata.height}`
            : null,
          updatedAt: new Date(project.updatedAt).toISOString(),
          createdAt: new Date(project.createdAt).toISOString(),
        })
      }

      setLocalProjectMap(nextLocalMap)

      // 保留所有后端项目；remoteProjectId 仅用于点击时跳转到本地编辑器。
      // 之前这里会把已有本地编辑副本的远端项目过滤掉，却没有加入对应替代项，
      // 导致用户进入编辑器后，刚创建的分片项目从项目列表中消失。
      setProjects([...items, ...localItems])
      setTotal(items.length + localItems.length)
    } catch {
      setLinkedEditorProjects({})
      setLocalProjectMap({})
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    const params = new URLSearchParams({
      page: '1',
      pageSize: '50',
      sort: sortBy,
    })
    if (filterType) params.set('type', filterType)
    if (filterStatus) params.set('status', filterStatus)
    if (filterSource) params.set('sourceInputType', filterSource)
    if (debouncedSearch) params.set('search', debouncedSearch)
    if (dateFrom) params.set('dateFrom', dateFrom)
    if (dateTo) params.set('dateTo', dateTo)

    void appFetch<ApiResponse<PaginatedResponse<ProjectListItem>>>(
      `/api/projects?${params.toString()}`,
    )
      .then((res) => {
        if (!cancelled) {
          setProjects(res.data.items)
          setTotal(res.data.total)
          void loadLinkedEditorProjects(res.data.items)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : '加载项目失败')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [filterType, filterStatus, filterSource, sortBy, debouncedSearch, dateFrom, dateTo, refreshKey, loadLinkedEditorProjects])

  async function handleRename(project: ProjectListItem) {
    const newName = window.prompt('输入新的项目名称', project.title)
    if (!newName || newName.trim() === project.title) return
    try {
      await appFetch<ApiResponse<unknown>>(`/api/projects/${project.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ title: newName.trim() }),
      })
      refresh()
    } catch (err) {
      alert(err instanceof AppApiError ? err.message : '重命名失败')
    }
  }

  async function handleArchive(project: ProjectListItem) {
    try {
      await appFetch<ApiResponse<unknown>>(`/api/projects/${project.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'archived' }),
      })
      refresh()
    } catch (err) {
      alert(err instanceof AppApiError ? err.message : '归档失败')
    }
  }

  async function handleDelete(project: ProjectListItem) {
    if (!window.confirm(`确定删除项目「${project.title}」吗？此操作不可撤销。`)) return
    try {
      await appFetch<ApiResponse<unknown>>(`/api/projects/${project.id}`, {
        method: 'DELETE',
      })
      refresh()
    } catch (err) {
      alert(err instanceof AppApiError ? err.message : '删除失败')
    }
  }

  return (
    <div className="px-6 py-6">
      {/* title row */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
          项目
        </h2>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="h-8 w-full rounded-xl border border-border bg-card py-2 pl-9 pr-4 text-sm text-foreground transition-colors placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="搜索项目..."
            />
          </div>
          <div className="inline-flex h-8 items-center gap-0.5 rounded-full border border-border bg-card p-0.5">
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                'flex size-7 items-center justify-center rounded-full transition-colors',
                viewMode === 'list' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              <List className="size-4" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={cn(
                'flex size-7 items-center justify-center rounded-full transition-colors',
                viewMode === 'grid' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              <LayoutGrid className="size-4" />
            </button>
          </div>
        </div>
      </div>

      {/* filter bar */}
      <div className="mt-2.5 flex items-center justify-between border-b border-border pb-2">
        <div className="flex min-w-0 flex-1 items-center gap-1">
          {/* 类型筛选 */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={cn(
                  'flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium leading-4 transition-colors',
                  filterType
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
              >
                {TYPE_OPTIONS.find((o) => o.value === filterType)?.label ?? '类型'}
                <ChevronDown className="size-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-36">
              {TYPE_OPTIONS.map((opt) => (
                <DropdownMenuItem
                  key={opt.value}
                  onSelect={() => setFilterType(opt.value)}
                  className="gap-2"
                >
                  <span className="flex-1">{opt.label}</span>
                  {filterType === opt.value ? <Check className="size-3.5" /> : null}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* 状态筛选 */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={cn(
                  'flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium leading-4 transition-colors',
                  filterStatus
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
              >
                {STATUS_OPTIONS.find((o) => o.value === filterStatus)?.label ?? '状态'}
                <ChevronDown className="size-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-36">
              {STATUS_OPTIONS.map((opt) => (
                <DropdownMenuItem
                  key={opt.value}
                  onSelect={() => setFilterStatus(opt.value)}
                  className="gap-2"
                >
                  <span className="flex-1">{opt.label}</span>
                  {filterStatus === opt.value ? <Check className="size-3.5" /> : null}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* 来源筛选 */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={cn(
                  'flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium leading-4 transition-colors',
                  filterSource
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
              >
                {SOURCE_OPTIONS.find((o) => o.value === filterSource)?.label ?? '来源'}
                <ChevronDown className="size-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-36">
              {SOURCE_OPTIONS.map((opt) => (
                <DropdownMenuItem
                  key={opt.value}
                  onSelect={() => setFilterSource(opt.value)}
                  className="gap-2"
                >
                  <span className="flex-1">{opt.label}</span>
                  {filterSource === opt.value ? <Check className="size-3.5" /> : null}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* 时间段筛选 */}
          <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
            <PopoverTrigger asChild>
              <button
                className={cn(
                  'flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium leading-4 transition-colors',
                  (dateFrom || dateTo)
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
              >
                <Calendar className="size-3" />
                {dateFrom || dateTo
                  ? `${dateFrom || '…'} ~ ${dateTo || '…'}`
                  : '时间段'}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72">
              <div className="space-y-3">
                <div className="text-sm font-medium text-foreground">选择时间段</div>
                <div className="space-y-2">
                  <label className="block">
                    <span className="text-xs text-muted-foreground">开始日期</span>
                    <input
                      type="date"
                      value={dateFrom}
                      max={dateTo || undefined}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="mt-1 h-8 w-full rounded-md border border-border bg-background px-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-muted-foreground">结束日期</span>
                    <input
                      type="date"
                      value={dateTo}
                      min={dateFrom || undefined}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="mt-1 h-8 w-full rounded-md border border-border bg-background px-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </label>
                </div>
                <div className="flex items-center justify-between pt-1">
                  <button
                    onClick={() => { setDateFrom(''); setDateTo('') }}
                    className="text-xs text-muted-foreground transition-colors hover:text-foreground"
                  >
                    清除
                  </button>
                  <Button
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setDatePopoverOpen(false)}
                  >
                    确定
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* 清除筛选 */}
          {(filterType || filterStatus || filterSource || dateFrom || dateTo) ? (
            <button
              onClick={() => { setFilterType(''); setFilterStatus(''); setFilterSource(''); setDateFrom(''); setDateTo('') }}
              className="ml-1 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              清除筛选
            </button>
          ) : null}
        </div>

        {/* 排序 */}
        <div className="ml-1 shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium leading-4 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                {SORT_OPTIONS.find((o) => o.value === sortBy)?.label ?? '排序'}
                <ChevronDown className="size-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-36">
              {SORT_OPTIONS.map((opt) => (
                <DropdownMenuItem
                  key={opt.value}
                  onSelect={() => setSortBy(opt.value)}
                  className="gap-2"
                >
                  <span className="flex-1">{opt.label}</span>
                  {sortBy === opt.value ? <Check className="size-3.5" /> : null}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* loading state */}
      {loading ? (
        <div className="mt-8 flex items-center justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-4 border-primary/20 border-t-primary" />
        </div>
      ) : error ? (
        <div className="mt-8 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      ) : projects.length === 0 ? (
        <div className="mt-8 flex flex-col items-center justify-center py-16 text-muted-foreground">
          <Clapperboard className="size-12 mb-4 opacity-50" />
          <p className="text-sm">暂无项目</p>
          <p className="mt-1 text-xs text-muted-foreground/70">
            点击左侧边栏「创建项目」按钮开始
          </p>
        </div>
      ) : viewMode === 'grid' ? (
        /* grid view */
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {projects.map((project) => (
            <div
              key={project.id}
              className="group cursor-pointer overflow-hidden rounded-lg border border-border bg-card transition-colors hover:bg-accent/50"
              onClick={() => goToProject(project)}
            >
              {/* 封面 */}
              <div className="relative aspect-video w-full overflow-hidden bg-muted">
                {resolveCoverUrl(project.coverUrl) ? (
                  <img src={resolveCoverUrl(project.coverUrl)!} alt={project.title} className="size-full object-cover" />
                ) : (
                  <div className="flex size-full items-center justify-center text-muted-foreground">
                    <Clapperboard className="size-8 opacity-40" />
                  </div>
                )}
                {/* 类型标签 */}
                <span className="absolute left-2 top-2 rounded-md bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">
                  {projectTypeLabel(project.type)}
                </span>
                {/* 操作菜单 */}
                <div className="absolute right-2 top-2 opacity-0 transition-opacity group-hover:opacity-100" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex size-6 items-center justify-center rounded-md bg-black/60 text-white backdrop-blur-sm transition-colors hover:bg-black/80">
                        <MoreHorizontal className="size-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      <DropdownMenuItem onSelect={() => handleRename(project)} className="gap-2">
                        <Pencil className="size-3.5" />
                        重命名
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => handleArchive(project)} className="gap-2">
                        <Archive className="size-3.5" />
                        归档
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onSelect={() => handleDelete(project)} className="gap-2 text-destructive focus:text-destructive">
                        <Trash2 className="size-3.5" />
                        删除
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              {/* 信息 */}
              <div className="p-2.5">
                <div className="truncate text-sm font-medium text-foreground">
                  {project.title}
                </div>
                {project.type === 'video_cut' ? (
                  <div className="mt-1 text-[11px] text-primary">
                    {linkedEditorProjects[project.id] ? '可直接进入专业编辑器' : '查看项目详情'}
                  </div>
                ) : null}
                <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{project.durationMs ? `${Math.round(project.durationMs / 1000)}秒` : '—'}</span>
                  <span>·</span>
                  <span>{new Date(project.updatedAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* table view */
        <div className="mt-4 overflow-hidden rounded-lg border border-border">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border text-[11px] font-medium tracking-wide text-muted-foreground">
                <th className="h-8 px-3 text-left font-medium">名称</th>
                <th className="hidden h-8 w-[100px] px-2 text-left font-medium md:table-cell">时长</th>
                <th className="hidden h-8 w-[100px] px-2 text-left font-medium md:table-cell">类型</th>
                <th className="hidden h-8 w-[140px] px-2 text-left font-medium md:table-cell">修改日期</th>
                <th className="h-8 w-[40px] px-2" />
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <tr
                  key={project.id}
                  className="cursor-pointer border-b border-border/50 transition-colors hover:bg-accent/50"
                  onClick={() => goToProject(project)}
                >
                  <td className="max-w-[500px] overflow-hidden px-3 py-2">
                    <div className="flex items-center gap-3">
                      <div className="relative size-9 w-16 shrink-0 overflow-hidden rounded-md bg-card">
                        {resolveCoverUrl(project.coverUrl) ? (
                          <img src={resolveCoverUrl(project.coverUrl)!} alt={project.title} className="size-full object-cover" />
                        ) : (
                          <div className="flex size-full items-center justify-center text-muted-foreground">
                            <Clapperboard className="size-4" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-foreground">
                          {project.title}
                        </div>
                        <div className="mt-0.5 flex items-center gap-1.5 text-muted-foreground">
                          <span className="text-xs">{project.projectNo}</span>
                          {project.type === 'video_cut' ? (
                            <span className="rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                              {linkedEditorProjects[project.id] ? '进入编辑器' : '查看详情'}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="hidden px-2 py-2 text-xs text-muted-foreground md:table-cell">
                    {project.durationMs ? `${Math.round(project.durationMs / 1000)}秒` : '—'}
                  </td>
                  <td className="hidden px-2 py-2 md:table-cell">
                    <span className="inline-block rounded-md bg-primary/15 px-1.5 py-0.5 text-xs font-medium text-primary">
                      {projectTypeLabel(project.type)}
                    </span>
                  </td>
                  <td className="hidden px-2 py-2 text-xs text-muted-foreground md:table-cell">
                    {new Date(project.updatedAt).toLocaleDateString()}
                  </td>
                  <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="flex items-center rounded p-0.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                        >
                          <MoreHorizontal className="size-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40">
                        <DropdownMenuItem
                          onSelect={() => handleRename(project)}
                          className="gap-2"
                        >
                          <Pencil className="size-3.5" />
                          重命名
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => handleArchive(project)}
                          className="gap-2"
                        >
                          <Archive className="size-3.5" />
                          归档
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onSelect={() => handleDelete(project)}
                          className="gap-2 text-destructive focus:text-destructive"
                        >
                          <Trash2 className="size-3.5" />
                          删除
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* count */}
      {!loading && !error && projects.length > 0 && (
        <div className="mt-4 text-xs text-muted-foreground">
          共 {total} 个项目
        </div>
      )}
    </div>
  )
}
